import { React, useEffect, useState, useRef } from 'react';
import './index2.css';
import '../App.css';
import './get-started.css';
import IntroVideo from '../images/intro_video.mp4';
import Crow_Logo from '../images/crow_electric3.png';
import VideoComponent from './VideoComponent.js';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useScrollBehavior } from './useScrollBehavior.js';
import SEO from '../SEO.js';
import Footer from './footer.js';

const LandingPage = () => {

  useScrollBehavior();

  const heroRef = useRef(null);
  const sectionRef = useRef(null);
  const offerRef = useRef(null);

  const [isHeroVisible, setIsHeroVisible] = useState(false);
  const [isSectionVisible, setIsSectionVisible] = useState(false);
  const [isOfferVisible, setIsOfferVisible] = useState(false);

  const subtitleRef = useRef(null);
  const isSubtitleInView = useInView(subtitleRef, { once: true, amount: 0.5 });

  const title = "Yokai Software | Websites That Bring You Customers";
  const description =
    "Custom web design, web development, SEO-focused structure, and digital solutions built to help businesses generate more leads and grow online.";
  const canonical = "https://yokaisoftware.com/landing";
  const image = "https://yokaisoftware.com/apple-touch-icon(1)_crow_logo.png";

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://yokaisoftware.com/#organization",
        name: "Yokai Software",
        url: "https://yokaisoftware.com/",
        logo: "https://yokaisoftware.com/apple-touch-icon(1)_crow_logo.png",
        description:
          "Yokai Software provides web design, web development, SEO, and digital solutions for businesses."
      },
      {
        "@type": "WebPage",
        "@id": "https://yokaisoftware.com/landing#webpage",
        url: "https://yokaisoftware.com/landing",
        name: "Yokai Software | Websites That Bring You Customers",
        description:
          "Websites designed for performance, visibility, and lead generation."
      }
    ]
  };

  const letterAnimation = {
    hidden: { opacity: 0 },
    visible: (i) => ({
      opacity: 1,
      transition: {
        delay: i * 0.05,
        duration: 0.4
      }
    })
  };

  const renderAnimatedLettersByWord = (text, isInView) => {
    const words = text.split(' ');
    let k = 0;

    return words.map((word, wi) => (
      <span className="wordWrap" key={wi}>
        {word.split('').map((char, ci) => (
          <motion.span
            key={`${wi}-${ci}`}
            custom={k++}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            variants={letterAnimation}
            className="char"
          >
            {char}
          </motion.span>
        ))}
        {wi < words.length - 1 ? ' ' : null}
      </span>
    ));
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut'
      }
    }
  };

  const handleScroll = () => {
    if (heroRef.current) {
      const rect = heroRef.current.getBoundingClientRect();
      setIsHeroVisible(rect.top < window.innerHeight - 80);
    }

    if (sectionRef.current) {
      const rect = sectionRef.current.getBoundingClientRect();
      setIsSectionVisible(rect.top < window.innerHeight - 120);
    }

    if (offerRef.current) {
      const rect = offerRef.current.getBoundingClientRect();
      setIsOfferVisible(rect.top < window.innerHeight - 120);
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div id="yokai_landing_wrapper">
  <SEO
    title={title}
    description={description}
    canonical={canonical}
    image={image}
    schema={schema}
  />

  <VideoComponent videoSrc={IntroVideo} />

  <div className="yokai_landing_module" id="yokai_landing_hero">
    <div id='yokai_landing_heading_container'>
      <div id='yokai_landing_heading_main' ref={subtitleRef}>
        {renderAnimatedLettersByWord("Websites", isSubtitleInView)}
      </div>

      <div id='yokai_landing_heading_accent'>
        {renderAnimatedLettersByWord("That Bring You Customers", isSubtitleInView)}
      </div>

      <div id='yokai_landing_heading_sub'>
        {renderAnimatedLettersByWord("Not Just Clicks.", isSubtitleInView)}
      </div>

      <motion.div
        id="yokai_landing_intro_text"
        ref={heroRef}
        initial="hidden"
        animate={isHeroVisible ? "visible" : "hidden"}
        variants={fadeUp}
      >
        <p>
          Yokai Software builds websites designed for trust, visibility, and conversion.
          If your current site is outdated, slow, or not generating leads, it is costing you business.
        </p>

        <div id="yokai_landing_cta_row">
          <Link to="/ContactPage" className="yokai_landing_cta_primary">
            Get My Website
          </Link>
          <Link to="/ContactPage" className="yokai_landing_cta_secondary">
            Free Website Audit
          </Link>
        </div>

        <div id="yokai_landing_pill_row">
          <div className="yokai_landing_pill">Custom Design</div>
          <div className="yokai_landing_pill">SEO Focused</div>
          <div className="yokai_landing_pill">Mobile First</div>
        </div>
      </motion.div>
    </div>

    <div id='yokai_landing_logo_container'>
      <div id="yokai_landing_logo">
        <img src={Crow_Logo} alt="Yokai Software logo" className="yokai_landing_logo_img" />
      </div>
    </div>
  </div>

      <div className="module" id="landing_module_problem">
        <div id="landing_problem_container" ref={sectionRef}>
          <motion.div
            id="landing_problem_heading"
            initial="hidden"
            animate={isSectionVisible ? "visible" : "hidden"}
            variants={fadeUp}
          >
            <h1>Most Websites Do Not Actually Help the Business</h1>
            <p>
              A site can look decent and still fail. Weak structure, poor mobile behavior,
              slow loading, and unclear messaging cause people to leave before they ever contact you.
            </p>
          </motion.div>

          <div id="landing_problem_cards">
            <motion.div
              className="landing_problem_card"
              initial="hidden"
              animate={isSectionVisible ? "visible" : "hidden"}
              variants={fadeUp}
              transition={{ delay: 0.15 }}
            >
              <h2>Slow Websites</h2>
              <p>
                Slow pages reduce trust and increase drop-off. Visitors leave before they engage.
              </p>
            </motion.div>

            <motion.div
              className="landing_problem_card"
              initial="hidden"
              animate={isSectionVisible ? "visible" : "hidden"}
              variants={fadeUp}
              transition={{ delay: 0.3 }}
            >
              <h2>Weak SEO Structure</h2>
              <p>
                If search engines cannot clearly understand your site, local customers will not find you.
              </p>
            </motion.div>

            <motion.div
              className="landing_problem_card"
              initial="hidden"
              animate={isSectionVisible ? "visible" : "hidden"}
              variants={fadeUp}
              transition={{ delay: 0.45 }}
            >
              <h2>Poor Conversion Flow</h2>
              <p>
                Confusing layout, weak headlines, and unclear calls to action kill leads.
              </p>
            </motion.div>
          </div>
        </div>
      </div>

      <div className="module" id="landing_module_solution">
        <div id="landing_solution_container">
          <div id="landing_solution_left">
            <h1>What Yokai Software Builds</h1>

            <div className="landing_solution_item">
              <h2>Custom design built for your business</h2>
              <p>
                Your company should not look like another template. The site should feel intentional and credible.
              </p>
            </div>

            <div className="landing_solution_item">
              <h2>SEO-aware page structure</h2>
              <p>
                Better hierarchy, better service positioning, and stronger content flow for visibility.
              </p>
            </div>

            <div className="landing_solution_item">
              <h2>Mobile-first experience</h2>
              <p>
                Most users are visiting on phones. The site needs to work there first.
              </p>
            </div>

            <div className="landing_solution_item">
              <h2>Conversion-focused layout</h2>
              <p>
                Better headlines, section order, and calls to action so visitors know what to do next.
              </p>
            </div>
          </div>

          <div id="landing_solution_right">
            <div className="landing_stat_box">
              <span>Built for</span>
              <h2>Lead Generation</h2>
            </div>
            <div className="landing_stat_box">
              <span>Designed for</span>
              <h2>Trust + Clarity</h2>
            </div>
            <div className="landing_stat_box">
              <span>Structured for</span>
              <h2>Search Visibility</h2>
            </div>
          </div>
        </div>
      </div>

      <div className="module" id="landing_module_offer">
        <motion.div
          id="landing_offer_box"
          ref={offerRef}
          initial="hidden"
          animate={isOfferVisible ? "visible" : "hidden"}
          variants={fadeUp}
        >
          <h1>Get Started Without Making It Complicated</h1>
          <p>
            Whether you need a full custom build, a redesign, or a stronger online presence,
            Yokai Software can help identify what is holding your site back and what to do next.
          </p>

          <div id="landing_offer_options">
            <div className="landing_offer_option">
              <h2>One-Time Build</h2>
              <p>
                Ideal if you want a complete website project planned, built, and launched.
              </p>
            </div>

            <div className="landing_offer_option">
              <h2>Ongoing Support</h2>
              <p>
                Ideal if you want updates, improvements, optimization, and continued help after launch.
              </p>
            </div>
          </div>

          <div id="landing_offer_button_row">
            <Link to="/ContactPage" className="landing_cta_button_primary">
              Start Your Project
            </Link>
          </div>
        </motion.div>
      </div>

      <div className="module" id="landing_module_final">
        <div id="landing_final_container">
          <h1>Let's Build Something That Actually Works</h1>
          <p>
            A stronger website improves trust, supports visibility, and helps turn traffic into real business.
          </p>
          <Link to="/ContactPage" className="landing_cta_button_primary">
            Contact Yokai Software
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default LandingPage;